import React from 'react';
import { connect } from 'react-redux';
import {
  Glyphicon,
  Navbar,
  Nav,
  NavItem as BootstrapNavItem,
  NavDropdown as BootstrapNavDropdown,
  Dropdown,
  MenuItem as BootstrapMenuItem
} from "react-bootstrap";
import { Link } from 'react-router-dom';
import igniteLogo from '../../assets/img/ignite_logo_black.png';
import { checkPermission } from '../common/guards/can';
import { getBrandsForNavbar } from '../../actions/brands';
import { getMyBasket } from '../../actions/orders';

// Needed since react-bootstrap passes a `href` prop, but <Link /> wants a `to` prop
// We also need to inject a handler for FINISH_REGISTRATION
const UnconnectedLink = ({ href, finishRegistration, ...props }) => (
  <Link to={href} {...props} onClick={finishRegistration} />
)

const WrappedLink = connect(
  null,
  { finishRegistration: () => dispatch => dispatch({ type: 'FINISH_REGISTRATION' }) }
)(UnconnectedLink);

const NavItem = props => <BootstrapNavItem {...props} componentClass={WrappedLink}/>;
const MenuItem = props => <BootstrapMenuItem {...props} componentClass={WrappedLink}/>;
const UnconnectedMenuItem = props => <BootstrapMenuItem {...props} componentClass={UnconnectedLink}/>;
// Alias for <DropdownButton/> (when rendered as a child there,
// <MenuItem/> doesn't accept a `componentClass` prop)
const DropdownItem = BootstrapMenuItem;
const UnconnectedDropdownItem = props => <BootstrapMenuItem {...props} componentClass={UnconnectedLink}/>;

class NavDropdown extends React.Component {
  state = { isOpen: false };

  handleToggle = (isOpen, ...args) => {
    this.setState({ isOpen });
    this.props.onToggle && this.props.onToggle(isOpen, ...args);
  }

  render() {
    const { title, onToggle, ...props } = this.props;
    const { isOpen } = this.state;

    return (
      <BootstrapNavDropdown
        open={isOpen}
        noCaret
        onToggle={this.handleToggle}
        title={
          <span>
            {title}
            <Glyphicon glyph={isOpen ? "minus" : "plus"}/>
          </span>
        }
        {...props}
      />
    );
  }
}

const UnconnectedMessageCount = ({ count, children }) => {
  if (count === 0) return <span>{children}</span>;

  return (
    <span className="navbar-has-message-count">
      {children}
      {' '}
      <span className="navbar-message-count">{count}</span>
    </span>
  )
}

const MessageCount = connect(
  ({ messages: { unreadMessagesCount }}) => ({ count: unreadMessagesCount || 0 })
)(UnconnectedMessageCount);

const NavbarBtn = ({ className: originalClasses, ...props }) => {
  // Remove `btn` and `btn-default` classes
  const className = originalClasses.split(/\s+/).filter(klass => !/^btn/.test(klass));
  return <button className={className} {...props}/>;
}

const UserNavigation = ({
  brands,
  basketCount,
  userName,
  getBrandsForNavbar,
  toggleBasket,
  hasCurrentStockItIncentiveEligibleOutlet,
  hasRewardOutlet, outlet
}) => (
  <Navbar fluid fixedTop collapseOnSelect className="is-user">
    <Navbar.Header>
      <Link to="/" className="navbar-brand">
        <img src={igniteLogo} alt="Ignite"/>
      </Link>
      <Navbar.Toggle/>

      <div className="navbar-extra-btns">
        {checkPermission("order", "stock") && (
          <button
            type="button"
            data-cy="basket-icon"
            className="user-navbar-btn"
            onClick={toggleBasket}
          >
            <Glyphicon glyph="shopping-cart"/>
            <span className="navbar-basket-count" data-cy="basket-count">
              {basketCount}
            </span>
          </button>
        )}

        <Dropdown id="navbar-user" className="user-navbar-dropdown">
          <Dropdown.Toggle noCaret componentClass={NavbarBtn}>
            <Glyphicon glyph="user"/>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <DropdownItem header>Hi {userName}</DropdownItem>
            <DropdownItem divider/>
            <UnconnectedDropdownItem href="/my-profile">My Profile</UnconnectedDropdownItem>
            <DropdownItem href="/logout">Log Out</DropdownItem>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Navbar.Header>

    <Navbar.Collapse>
      <Nav>
        {checkPermission("order,read", "stock") && (
          <NavItem href="/order/new">Order Now</NavItem>
        )}

        <NavDropdown
          title={<MessageCount>My Ignite</MessageCount>}
          id="navbar-my-ignite"
        >
          {checkPermission("view", "objective_info,objective_performance") && (
            <MenuItem href="/store-performances">
              Sales Incentive
            </MenuItem>
          )}
          {checkPermission("handle", "staff_rewards") && (
            <UnconnectedMenuItem href="/my-incentives" >
              My Incentives
            </UnconnectedMenuItem>
          )}
          {hasRewardOutlet && (
            <UnconnectedMenuItem href="/my-rewards" >
              My Rewards
            </UnconnectedMenuItem>
          )}
          {checkPermission("view", "objective_performance") && (
            <MenuItem href="/points-statement#staff">
              My Points Statement
            </MenuItem>
          )}
          <MenuItem href="/store-summary">
            My Store Summary
          </MenuItem>
          {checkPermission("order", "stock") && (
            <MenuItem href="/my-orders#complete">
              My Past Orders
            </MenuItem>
          )}
          {checkPermission("handle", "staff") && (
            <MenuItem href="/my-staff">
              My Staff
            </MenuItem>
          )}
          <MenuItem href="/about-ignite">
            My Partnership
          </MenuItem>
          <MenuItem href="/my-messages">
            <MessageCount>My Messages</MessageCount>
          </MenuItem>
          {checkPermission('view', 'Assessment') && (
            <MenuItem href="/assessments">
              Assessments
            </MenuItem>
          )}
          {hasCurrentStockItIncentiveEligibleOutlet && (
            <MenuItem href="/stock-it-incentives">
              Stock It Incentive
            </MenuItem>
          )}
        </NavDropdown>

        <NavDropdown
          title="Our Brands"
          id="navbar-our-brands"
          onToggle={isOpen => {
            // We assume that there will always be at least 1 brand in the db
            isOpen && !brands.length && getBrandsForNavbar()
          }}
        >
          {brands.map(({ id, attributes: { name }, links: { page }}) => (
            <MenuItem key={id} href={page}>{name}</MenuItem>
          ))}
          {/* Explicitly pass `null` to avoid a React propType warning about NavDropdown#children */}
          {Boolean(brands.length) ? null : <MenuItem href="#" disabled>Loading...</MenuItem>}
        </NavDropdown>

        <NavItem href="/latest-news">News</NavItem>
        <NavItem href="/contact-us">Contact Us</NavItem>
        <NavItem href={outlet ? `/store-details/${outlet}` : "#"}>My rep</NavItem>
      </Nav>
    </Navbar.Collapse>
  </Navbar>
);

const mapStateToProps = ({
  brands: { brands },
  orders: { new: { basket }},
  viewConfig: { profile: user }
}) => {
  const defaultOutletId = user.attributes['default-outlet-id'];
  const currentOutletId = user.attributes['current-outlet'] ? user.attributes['current-outlet'].id : null;

  return {
    brands,
    basketCount: Object.values(basket.line_items).reduce((sum, { product_qty }) => sum + product_qty, 0),
    userName: `${user.attributes['first-name']} ${user.attributes['last-name']}`,
    hasCurrentStockItIncentiveEligibleOutlet: user.attributes['has-current-stock-it-incentive-eligible-outlet'],
    hasRewardOutlet: user.attributes['has-reward-outlet'],
    outlet: defaultOutletId || currentOutletId
  };
};

const toggleBasket = () => (dispatch, getState) => {
  const { showBasketDropdown } = getState().viewConfig;

  if (showBasketDropdown) {
    dispatch({ type: 'TOGGLE_BASKET_DROPDOWN' });
  } else {
    dispatch(getMyBasket()).then(() => dispatch({ type: 'TOGGLE_BASKET_DROPDOWN' }));
  }
}

export default connect(
  mapStateToProps,
  { getBrandsForNavbar, toggleBasket }
)(UserNavigation);



// WEBPACK FOOTER //
// ./src/components/layout/user-navigation.jsx